import { useState, useEffect } from 'react';
import { StaffSidebar } from '../../components/StaffSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Calendar } from '../../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { format, addDays, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, isSameDay } from 'date-fns';
import { Plus, Clock, MapPin, ChevronLeft, ChevronRight, Trash2, Edit } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeOptions = [
  '06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00',
  '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00',
  '20:00', '21:00', '22:00'
];

export default function StaffAvailability() {
  const { token, user } = useAuth();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [availabilities, setAvailabilities] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAvailability, setEditingAvailability] = useState(null);
  const [formData, setFormData] = useState({
    branch_id: '',
    date: '',
    start_time: '09:00',
    end_time: '17:00',
    is_available: true,
    note: ''
  });

  useEffect(() => {
    fetchData();
  }, [currentMonth]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const startDate = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
      const endDate = format(endOfMonth(currentMonth), 'yyyy-MM-dd');
      
      const [availRes, branchesRes] = await Promise.all([
        axios.get(`${API}/staff/availability?start_date=${startDate}&end_date=${endDate}`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }),
        axios.get(`${API}/branches`)
      ]);
      
      setAvailabilities(availRes.data);
      setBranches(branchesRes.data);
    } catch (error) {
      toast.error('Failed to fetch availability data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.branch_id || !formData.date) {
      toast.error('Please select a branch and date');
      return;
    }

    try {
      if (editingAvailability) {
        await axios.put(
          `${API}/staff/availability/${editingAvailability.availability_id}`,
          {
            start_time: formData.start_time,
            end_time: formData.end_time,
            is_available: formData.is_available,
            note: formData.note || null
          },
          {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true
          }
        );
        toast.success('Availability updated');
      } else {
        await axios.post(
          `${API}/staff/availability`,
          formData,
          {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true
          }
        );
        toast.success('Availability added');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save availability');
    }
  };

  const handleDelete = async (availabilityId) => {
    if (!window.confirm('Delete this availability?')) return;
    
    try {
      await axios.delete(`${API}/staff/availability/${availabilityId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success('Availability deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete availability');
    }
  };

  const openEditDialog = (availability) => {
    setEditingAvailability(availability);
    setFormData({
      branch_id: availability.branch_id,
      date: availability.date,
      start_time: availability.start_time,
      end_time: availability.end_time,
      is_available: availability.is_available,
      note: availability.note || ''
    });
    setDialogOpen(true);
  };

  const openAddDialog = (date) => {
    setEditingAvailability(null);
    setFormData({
      branch_id: branches[0]?.branch_id || '',
      date: format(date, 'yyyy-MM-dd'),
      start_time: '09:00',
      end_time: '17:00',
      is_available: true,
      note: ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingAvailability(null);
    setFormData({
      branch_id: '',
      date: '',
      start_time: '09:00',
      end_time: '17:00',
      is_available: true,
      note: ''
    });
  };

  // Generate calendar days
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
  
  // Get day of week for first day (0 = Sunday)
  const startDayOfWeek = monthStart.getDay();
  
  // Add empty slots for days before month starts
  const paddingDays = Array(startDayOfWeek).fill(null);

  const getAvailabilityForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return availabilities.filter(a => a.date === dateStr);
  };

  const prevMonth = () => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1));
  const nextMonth = () => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1));

  return (
    <div className="min-h-screen bg-warm-sand">
      <StaffSidebar />
      
      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="p-6 lg:p-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading text-3xl font-bold text-deep-charcoal">
                Availability
              </h1>
              <p className="font-body text-deep-charcoal/60 mt-1">
                Manage your work schedule across branches
              </p>
            </div>
            
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="rounded-xl bg-terracotta text-white hover:bg-terracotta-dark" data-testid="add-availability-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Availability
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md bg-white">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl">
                    {editingAvailability ? 'Edit Availability' : 'Add Availability'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label className="font-body text-sm">Branch</Label>
                    <Select
                      value={formData.branch_id}
                      onValueChange={(value) => setFormData({ ...formData, branch_id: value })}
                      disabled={!!editingAvailability}
                    >
                      <SelectTrigger className="rounded-xl" data-testid="availability-branch-select">
                        <SelectValue placeholder="Select branch" />
                      </SelectTrigger>
                      <SelectContent>
                        {branches.map((branch) => (
                          <SelectItem key={branch.branch_id} value={branch.branch_id}>
                            {branch.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="font-body text-sm">Date</Label>
                    <Calendar
                      mode="single"
                      selected={formData.date ? new Date(formData.date + 'T00:00:00') : undefined}
                      onSelect={(date) => date && setFormData({ ...formData, date: format(date, 'yyyy-MM-dd') })}
                      disabled={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return date < today || !!editingAvailability;
                      }}
                      className="rounded-xl border border-deep-charcoal/10 p-3"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="font-body text-sm">Start Time</Label>
                      <Select
                        value={formData.start_time}
                        onValueChange={(value) => setFormData({ ...formData, start_time: value })}
                      >
                        <SelectTrigger className="rounded-xl" data-testid="start-time-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {timeOptions.map((time) => (
                            <SelectItem key={time} value={time}>{time}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="font-body text-sm">End Time</Label>
                      <Select
                        value={formData.end_time}
                        onValueChange={(value) => setFormData({ ...formData, end_time: value })}
                      >
                        <SelectTrigger className="rounded-xl" data-testid="end-time-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {timeOptions.map((time) => (
                            <SelectItem key={time} value={time}>{time}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <Label className="font-body text-sm">Available for bookings</Label>
                    <Switch
                      checked={formData.is_available}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_available: checked })}
                      data-testid="availability-toggle"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="font-body text-sm">Note (optional)</Label>
                    <Textarea
                      value={formData.note}
                      onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                      className="rounded-xl"
                      placeholder="e.g., Available for spa services only"
                      data-testid="availability-note"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full rounded-xl bg-terracotta text-white hover:bg-terracotta-dark"
                    data-testid="save-availability-btn"
                  >
                    {editingAvailability ? 'Update' : 'Add'} Availability
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-terracotta"></div>
            </div>
          ) : (
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Calendar */}
              <Card className="lg:col-span-2 bg-white rounded-xl border-0 shadow-soft">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading text-xl text-deep-charcoal">
                      {format(currentMonth, 'MMMM yyyy')}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={prevMonth}
                        className="rounded-lg"
                        data-testid="prev-month-btn"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={nextMonth}
                        className="rounded-lg"
                        data-testid="next-month-btn"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Day headers */}
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                      <div key={day} className="text-center font-body text-xs text-deep-charcoal/50 py-2">
                        {day}
                      </div>
                    ))}
                  </div>
                  
                  {/* Calendar grid */}
                  <div className="grid grid-cols-7 gap-1">
                    {paddingDays.map((_, index) => (
                      <div key={`pad-${index}`} className="h-24" />
                    ))}
                    {calendarDays.map((day) => {
                      const dayAvailabilities = getAvailabilityForDate(day);
                      const isSelected = selectedDate && isSameDay(day, selectedDate);
                      
                      return (
                        <button
                          key={day.toISOString()}
                          onClick={() => setSelectedDate(day)}
                          data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
                          className={`h-24 p-1 rounded-lg border transition-all text-left ${
                            isSelected 
                              ? 'border-terracotta bg-terracotta/5' 
                              : 'border-transparent hover:border-deep-charcoal/10 hover:bg-warm-sand/50'
                          } ${isToday(day) ? 'ring-2 ring-sage ring-offset-1' : ''}`}
                        >
                          <div className={`font-body text-sm mb-1 ${
                            isToday(day) ? 'text-sage font-semibold' : 'text-deep-charcoal'
                          }`}>
                            {format(day, 'd')}
                          </div>
                          <div className="space-y-0.5 overflow-hidden">
                            {dayAvailabilities.slice(0, 2).map((avail) => (
                              <div
                                key={avail.availability_id}
                                className={`text-xs px-1 py-0.5 rounded truncate ${
                                  avail.is_available 
                                    ? 'bg-sage/20 text-sage' 
                                    : 'bg-red-100 text-red-600'
                                }`}
                              >
                                {avail.start_time}
                              </div>
                            ))}
                            {dayAvailabilities.length > 2 && (
                              <div className="text-xs text-deep-charcoal/50 px-1">
                                +{dayAvailabilities.length - 2} more
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Selected Day Details */}
              <Card className="bg-white rounded-xl border-0 shadow-soft">
                <CardHeader>
                  <CardTitle className="font-heading text-lg text-deep-charcoal">
                    {selectedDate 
                      ? format(selectedDate, 'EEEE, MMMM d') 
                      : 'Select a date'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {selectedDate ? (
                    <>
                      <Button
                        onClick={() => openAddDialog(selectedDate)}
                        variant="outline"
                        className="w-full mb-4 rounded-xl border-dashed border-deep-charcoal/20"
                        data-testid="add-for-date-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add for this date
                      </Button>
                      
                      {getAvailabilityForDate(selectedDate).length === 0 ? (
                        <p className="font-body text-sm text-deep-charcoal/50 text-center py-8">
                          No availability set for this date
                        </p>
                      ) : (
                        <div className="space-y-3">
                          {getAvailabilityForDate(selectedDate).map((avail) => (
                            <div 
                              key={avail.availability_id}
                              className={`p-4 rounded-xl border ${
                                avail.is_available 
                                  ? 'border-sage/30 bg-sage/5' 
                                  : 'border-red-200 bg-red-50'
                              }`}
                              data-testid={`availability-item-${avail.availability_id}`}
                            >
                              <div className="flex items-start justify-between mb-2">
                                <Badge className={avail.is_available 
                                  ? 'bg-sage/20 text-sage border-sage/30' 
                                  : 'bg-red-100 text-red-600 border-red-200'
                                }>
                                  {avail.is_available ? 'Available' : 'Unavailable'}
                                </Badge>
                                <div className="flex gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openEditDialog(avail)}
                                    className="h-8 w-8 p-0"
                                    data-testid={`edit-${avail.availability_id}`}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDelete(avail.availability_id)}
                                    className="h-8 w-8 p-0 text-red-500 hover:text-red-600"
                                    data-testid={`delete-${avail.availability_id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                              
                              <div className="space-y-1">
                                <div className="flex items-center text-deep-charcoal/70">
                                  <MapPin className="w-4 h-4 mr-2 text-terracotta" />
                                  <span className="font-body text-sm truncate">{avail.branch_name}</span>
                                </div>
                                <div className="flex items-center text-deep-charcoal/70">
                                  <Clock className="w-4 h-4 mr-2 text-terracotta" />
                                  <span className="font-body text-sm">
                                    {avail.start_time} - {avail.end_time}
                                  </span>
                                </div>
                                {avail.note && (
                                  <p className="font-body text-xs text-deep-charcoal/50 italic mt-2">
                                    {avail.note}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="font-body text-sm text-deep-charcoal/50 text-center py-8">
                      Click on a date to view or add availability
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
